<template>
  <section class="q-notes" style="margin-bottom: 20vh">
    <div class="container q-wrap">
      <!-- Header -->
      <div class="text-center q-head">
        <span class="q-pill">
          <i class="bi bi-camera me-2"></i>Analyze Notes
        </span>

        <h2 class="q-title mt-3">
          Upload notes.
          Get a <span class="q-grad">quiz</span>, <span class="q-grad">summary</span>,
          and a <span class="q-grad">study plan</span>.
        </h2>

        <p class="q-sub mt-3 mx-auto">
          This is a UI-only mock. It accepts an image/PDF and generates a fake analysis preview so your app feels real.
        </p>
      </div>

      <div class="row g-4 align-items-start mt-4">
        <!-- Left -->
        <div class="col-12 col-lg-6">
          <div class="q-card">
            <div class="q-card-top">
              <div class="q-badge">
                <i class="bi bi-shield-lock me-2"></i>Private by default
              </div>
              <div class="q-card-title">Upload your notes</div>
              <div class="q-card-sub">PNG, JPG, or PDF. We’ll “analyze” and generate study material.</div>
            </div>

            <!-- Dropzone -->
            <div
              class="q-drop mt-4"
              :class="{ 'q-drop-active': dragging }"
              @dragenter.prevent="dragging = true"
              @dragover.prevent="dragging = true"
              @dragleave.prevent="dragging = false"
              @drop.prevent="onDrop"
            >
              <div v-if="!file" class="q-drop-inner">
                <div class="q-drop-ic"><i class="bi bi-cloud-arrow-up"></i></div>
                <div class="q-drop-title mt-3">Drop your notes here</div>
                <div class="q-drop-sub">or click to upload</div>

                <input
                  ref="fileInput"
                  class="q-file"
                  type="file"
                  accept="image/*,application/pdf"
                  @change="onPick"
                />

                <div class="d-flex flex-wrap gap-3 justify-content-center mt-4">
                  <button class="btn q-btn" type="button" @click="openPicker">
                    <i class="bi bi-upload me-2"></i>Upload file
                  </button>
                  <button class="btn q-btn-soft" type="button" @click="useDemo">
                    <i class="bi bi-stars me-2"></i>Use demo file
                  </button>
                </div>
              </div>

              <div v-else class="q-file-picked">
                <div class="q-file-row">
                  <div class="q-file-ic"><i class="bi bi-file-earmark-text"></i></div>
                  <div class="q-file-meta">
                    <div class="q-file-name">{{ file.name }}</div>
                    <div class="q-file-sub">{{ prettyType }} • {{ prettySize }}</div>
                  </div>
                  <button class="btn q-x" type="button" @click="clearFile" aria-label="Remove file">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>

                <div v-if="previewUrl" class="q-preview mt-3">
                  <img v-if="isImage" :src="previewUrl" alt="Preview" class="q-preview-img" />
                  <div v-else class="q-preview-pdf">
                    <i class="bi bi-filetype-pdf me-2"></i>PDF selected (preview disabled)
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-3 justify-content-center mt-4">
                  <button class="btn q-cta" type="button" :disabled="loading" @click="analyze">
                    <span v-if="!loading"><i class="bi bi-magic me-2"></i>Analyze</span>
                    <span v-else><span class="spinner-border spinner-border-sm me-2" role="status"></span>Analyzing…</span>
                  </button>

                  <button class="btn q-secondary" type="button" :disabled="loading" @click="openPicker">
                    <i class="bi bi-arrow-repeat me-2"></i>Choose another
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Settings -->
          <div class="q-card mt-4">
            <div class="q-card-title">Options</div>
            <div class="q-card-sub">These don’t change anything yet — just UI.</div>

            <div class="row g-3 mt-3">
              <!-- Output (custom dropdown) -->
              <div class="col-12 col-md-6">
                <label class="q-label">Output</label>

                <div class="q-ddwrap" data-dd="output">
                  <button
                    class="q-ddbtn"
                    type="button"
                    @click="toggleDd('output')"
                    @keydown.enter.prevent="toggleDd('output')"
                    @keydown.esc.prevent="closeDd()"
                    aria-haspopup="listbox"
                    :aria-expanded="openDd === 'output'"
                  >
                    <span class="q-ddbtn-left">
                      <span class="q-ddicon"><i class="bi bi-sliders2"></i></span>
                      <span class="q-ddtext">
                        <span class="q-ddtop">Output</span>
                        <span class="q-ddbottom">{{ labelFor(outputOptions, options.output) }}</span>
                      </span>
                    </span>
                    <span class="q-ddchev" :class="{ on: openDd === 'output' }">
                      <i class="bi bi-chevron-down"></i>
                    </span>
                  </button>

                  <div v-show="openDd === 'output'" class="q-ddmenu" role="listbox">
                    <button
                      v-for="opt in outputOptions"
                      :key="opt.value"
                      class="q-dditem"
                      type="button"
                      @click="pick('output', opt.value)"
                      :class="{ active: options.output === opt.value }"
                      role="option"
                      :aria-selected="options.output === opt.value"
                    >
                      <span class="q-dditem-title">{{ opt.label }}</span>
                      <span v-if="opt.hint" class="q-dditem-hint">{{ opt.hint }}</span>
                      <i v-if="options.output === opt.value" class="bi bi-check2 q-ddcheck"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Difficulty (custom dropdown) -->
              <div class="col-12 col-md-6">
                <label class="q-label">Difficulty</label>

                <div class="q-ddwrap" data-dd="difficulty">
                  <button
                    class="q-ddbtn"
                    type="button"
                    @click="toggleDd('difficulty')"
                    @keydown.enter.prevent="toggleDd('difficulty')"
                    @keydown.esc.prevent="closeDd()"
                    aria-haspopup="listbox"
                    :aria-expanded="openDd === 'difficulty'"
                  >
                    <span class="q-ddbtn-left">
                      <span class="q-ddicon"><i class="bi bi-bar-chart-steps"></i></span>
                      <span class="q-ddtext">
                        <span class="q-ddtop">Difficulty</span>
                        <span class="q-ddbottom">{{ labelFor(difficultyOptions, options.difficulty) }}</span>
                      </span>
                    </span>
                    <span class="q-ddchev" :class="{ on: openDd === 'difficulty' }">
                      <i class="bi bi-chevron-down"></i>
                    </span>
                  </button>

                  <div v-show="openDd === 'difficulty'" class="q-ddmenu" role="listbox">
                    <button
                      v-for="opt in difficultyOptions"
                      :key="opt.value"
                      class="q-dditem"
                      type="button"
                      @click="pick('difficulty', opt.value)"
                      :class="{ active: options.difficulty === opt.value }"
                      role="option"
                      :aria-selected="options.difficulty === opt.value"
                    >
                      <span class="q-dditem-title">{{ opt.label }}</span>
                      <span v-if="opt.hint" class="q-dditem-hint">{{ opt.hint }}</span>
                      <i v-if="options.difficulty === opt.value" class="bi bi-check2 q-ddcheck"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <label class="q-label">Study deadline (optional)</label>
                <input class="form-control q-input" type="date" v-model="options.deadline" />
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Results (unchanged) -->
        <div class="col-12 col-lg-6">
          <div class="q-card q-results">
            <div class="q-results-top">
              <div>
                <div class="q-card-title">Results</div>
                <div class="q-card-sub">You’ll see generated content here after analyzing.</div>
              </div>

              <div class="q-status" :class="{ 'q-status-on': analyzed }">
                <span class="q-status-dot"></span>
                <span>{{ analyzed ? "Ready" : "Waiting" }}</span>
              </div>
            </div>

            <div v-if="!analyzed" class="q-empty mt-4">
              <div class="q-empty-ic"><i class="bi bi-inbox"></i></div>
              <div class="q-empty-title mt-3">No analysis yet</div>
              <div class="q-empty-sub">Upload a file and click Analyze to generate a preview.</div>
            </div>

            <div v-else class="mt-4">
              <div class="q-tabs">
                <button class="btn q-tab" :class="{ active: tab === 'summary' }" @click="tab = 'summary'">Summary</button>
                <button class="btn q-tab" :class="{ active: tab === 'quiz' }" @click="tab = 'quiz'">Mini Quiz</button>
                <button class="btn q-tab" :class="{ active: tab === 'plan' }" @click="tab = 'plan'">Study Plan</button>
              </div>

              <div v-if="tab === 'summary'" class="q-pane mt-3">
                <div class="q-pane-title"><i class="bi bi-journal-text me-2"></i>Short Notes</div>
                <ul class="q-bullets mt-3">
                  <li v-for="(b, i) in mock.summary" :key="i">{{ b }}</li>
                </ul>

                <div class="q-actions mt-4">
                  <button class="btn q-secondary" type="button" @click="copyText(mock.summary.join('\n'))">
                    <i class="bi bi-copy me-2"></i>Copy
                  </button>
                  <button class="btn q-download" type="button" @click="downloadTxt('summary', mock.summary.join('\n'))">
                    <i class="bi bi-download me-2"></i>Download .txt
                  </button>
                </div>
              </div>

              <div v-if="tab === 'quiz'" class="q-pane mt-3">
                <div class="q-pane-title"><i class="bi bi-question-circle me-2"></i>Mini Quiz</div>

                <div class="q-quiz mt-3">
                  <div v-for="(q, idx) in mock.quiz" :key="idx" class="q-q">
                    <div class="q-q-title">
                      <span class="q-q-num">{{ idx + 1 }}</span>
                      <span>{{ q.q }}</span>
                    </div>
                    <div class="q-opts mt-2">
                      <label v-for="(opt, j) in q.options" :key="j" class="q-opt">
                        <input class="form-check-input me-2" type="radio" :name="'q' + idx" :value="opt" v-model="answers[idx]" />
                        <span>{{ opt }}</span>
                      </label>
                    </div>

                    <div v-if="showAnswers" class="q-answer mt-2">
                      <i class="bi bi-check2-circle me-2"></i>
                      Correct: <b>{{ q.answer }}</b>
                    </div>
                  </div>
                </div>

                <div class="q-actions mt-4">
                  <button class="btn q-secondary" type="button" @click="showAnswers = !showAnswers">
                    <i class="bi bi-eye me-2"></i>{{ showAnswers ? "Hide" : "Show" }} answers
                  </button>
                  <button class="btn q-download" type="button" @click="downloadTxt('quiz', quizAsText)">
                    <i class="bi bi-download me-2"></i>Download .txt
                  </button>
                </div>
              </div>

              <div v-if="tab === 'plan'" class="q-pane mt-3">
                <div class="q-pane-title"><i class="bi bi-calendar2-check me-2"></i>Study Plan</div>

                <div class="q-plan mt-3">
                  <div v-for="(d, i) in mock.plan" :key="i" class="q-day">
                    <div class="q-day-left">
                      <div class="q-day-title">{{ d.day }}</div>
                      <div class="q-day-sub">{{ d.focus }}</div>
                    </div>
                    <span class="q-chip">{{ d.time }}</span>
                  </div>
                </div>

                <div class="q-actions mt-4">
                  <button class="btn q-secondary" type="button" @click="copyText(planAsText)">
                    <i class="bi bi-copy me-2"></i>Copy
                  </button>
                  <button class="btn q-download" type="button" @click="downloadTxt('plan', planAsText)">
                    <i class="bi bi-download me-2"></i>Download .txt
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="q-card mt-4 q-upsell">
            <div class="q-upsell-title">
              <i class="bi bi-box2-heart me-2"></i>Unlock more
            </div>
            <div class="q-upsell-sub">
              Want the full study plan + smart assistant? That’s part of your bundle add-on.
            </div>

            <div class="d-flex gap-3 flex-wrap mt-3">
              <a class="btn q-secondary" href="/pricing">View Pricing</a>
              <a class="btn q-cta" href="/downloads">Start Free Trial</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Click-outside overlay (only when a dropdown is open) -->
    <button
      v-if="openDd"
      class="q-ddbackdrop"
      type="button"
      aria-label="Close dropdown"
      @click="closeDd"
    ></button>
  </section>
</template>

<script>
export default {
  name: "AnalyzeNotesView",
  data() {
    return {
      dragging: false,
      file: null,
      previewUrl: null,

      loading: false,
      analyzed: false,
      tab: "summary",
      showAnswers: false,
      answers: {},

      // dropdown state
      openDd: null,

      outputOptions: [
        { value: "all", label: "Quiz + Summary + Plan", hint: "Full bundle output" },
        { value: "quiz", label: "Quiz only", hint: "Questions + answers" },
        { value: "summary", label: "Summary only", hint: "Short notes + key points" },
        { value: "plan", label: "Study plan only", hint: "Schedule + tasks" },
      ],
      difficultyOptions: [
        { value: "easy", label: "Easy", hint: "Warm-up level" },
        { value: "medium", label: "Medium", hint: "Balanced difficulty" },
        { value: "hard", label: "Hard", hint: "Exam mode" },
      ],

      options: {
        output: "all",
        difficulty: "medium",
        deadline: "",
      },

      mock: {
        summary: [
          "Key concept: Identify the main idea and supporting details.",
          "Important terms: Define keywords and note examples.",
          "Process: Break the topic into steps and test yourself with quick questions.",
          "Memory trick: Summarize each section in one sentence.",
        ],
        quiz: [
          {
            q: "What is the purpose of a short summary?",
            options: ["To copy notes word-for-word", "To capture key ideas quickly", "To avoid studying", "To replace practice"],
            answer: "To capture key ideas quickly",
          },
          {
            q: "Which method improves retention the most?",
            options: ["Passive reading", "Active recall", "Skipping practice", "Only highlighting"],
            answer: "Active recall",
          },
          {
            q: "A good study plan should primarily be…",
            options: ["Random", "Consistent and realistic", "Only one long session", "No deadlines"],
            answer: "Consistent and realistic",
          },
        ],
        plan: [
          { day: "Day 1", focus: "Scan notes → extract key terms → create 10 flashcards", time: "30–45 min" },
          { day: "Day 2", focus: "Mini quiz → review wrong answers → re-summarize", time: "30 min" },
          { day: "Day 3", focus: "Practice questions → deepen weak topics", time: "45 min" },
          { day: "Day 4", focus: "Mixed revision → short quiz + recap", time: "30 min" },
        ],
      },
    };
  },
  computed: {
    isImage() {
      return this.file && this.file.type && this.file.type.startsWith("image/");
    },
    prettyType() {
      if (!this.file) return "";
      if (this.isImage) return "Image";
      if (this.file.type === "application/pdf") return "PDF";
      return this.file.type || "File";
    },
    prettySize() {
      if (!this.file) return "";
      const kb = this.file.size / 1024;
      if (kb < 1024) return `${kb.toFixed(0)} KB`;
      const mb = kb / 1024;
      return `${mb.toFixed(2)} MB`;
    },
    planAsText() {
      return this.mock.plan.map((d) => `${d.day}: ${d.focus} (${d.time})`).join("\n");
    },
    quizAsText() {
      return this.mock.quiz
        .map((q, i) => {
          const opts = q.options.map((o) => `  - ${o}`).join("\n");
          return `${i + 1}. ${q.q}\n${opts}\nAnswer: ${q.answer}\n`;
        })
        .join("\n");
    },
  },
  methods: {
    // dropdown helpers
    labelFor(list, value) {
      return list.find((x) => x.value === value)?.label || "Select…";
    },
    toggleDd(which) {
      this.openDd = this.openDd === which ? null : which;
    },
    closeDd() {
      this.openDd = null;
    },
    pick(which, value) {
      this.options[which] = value;
      this.openDd = null;
    },

    openPicker() {
      this.$refs.fileInput?.click();
    },
    onPick(e) {
      const f = e.target.files?.[0];
      if (!f) return;
      this.setFile(f);
    },
    onDrop(e) {
      this.dragging = false;
      const f = e.dataTransfer?.files?.[0];
      if (!f) return;
      this.setFile(f);
    },
    setFile(f) {
      const ok = f.type.startsWith("image/") || f.type === "application/pdf";
      if (!ok) {
        alert("Please upload an image or PDF.");
        return;
      }

      this.clearPreview();
      this.file = f;

      if (f.type.startsWith("image/")) this.previewUrl = URL.createObjectURL(f);
      else this.previewUrl = null;

      this.analyzed = false;
      this.tab = "summary";
      this.showAnswers = false;
      this.answers = {};
    },
    clearPreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    },
    clearFile() {
      this.clearPreview();
      this.file = null;
      this.analyzed = false;
      this.answers = {};
      this.showAnswers = false;
      this.tab = "summary";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    useDemo() {
      alert("Demo file is UI-only. Please upload an image/PDF for the preview.");
      this.clearFile();
    },
    async analyze() {
      if (!this.file) return;
      this.loading = true;
      await new Promise((r) => setTimeout(r, 900));
      this.analyzed = true;
      this.tab = "summary";
      this.showAnswers = false;
      this.answers = {};
      this.loading = false;
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
    },
    downloadTxt(kind, text) {
      const now = new Date().toISOString().slice(0, 10);
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `quick-quiz-ai-${kind}-${now}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    },
  },
  mounted() {
    // ESC closes dropdown anywhere
    this._onKey = (e) => {
      if (e.key === "Escape") this.openDd = null;
    };
    window.addEventListener("keydown", this._onKey);
  },
  beforeUnmount() {
    this.clearPreview();
    window.removeEventListener("keydown", this._onKey);
  },
};
</script>

<style scoped>
.q-notes { padding: 2.75rem 0 4.5rem; }
.q-wrap { max-width: 72rem; }
.q-head { max-width: 56rem; margin-left: auto; margin-right: auto; }

.q-pill{
  display:inline-flex;align-items:center;gap:0.5rem;
  padding:0.5rem 0.9rem;border-radius:62.5rem;
  background:rgba(255,255,255,0.06);
  border:0.0625rem solid rgba(255,255,255,0.1);
  color:rgba(255,255,255,0.85);
  font-weight:900;font-size:0.95rem;
}

.q-title{
  font-size:clamp(2rem,3.2vw,3rem);
  font-weight:950;letter-spacing:-0.06rem;line-height:1.05;
  color:rgba(255,255,255,0.96);
}

.q-grad{
  background:linear-gradient(90deg,#2d6cff 0%,#7c3aed 55%,#ff3d8d 120%);
  -webkit-background-clip:text;background-clip:text;color:transparent;
}

.q-sub{font-size:1.05rem;line-height:1.65;color:rgba(255,255,255,0.72);max-width:92%;}

/* Card */
.q-card{
  border-radius:1.75rem;padding:1.6rem;
  background:rgba(255,255,255,0.04);
  border:0.0625rem solid rgba(255,255,255,0.08);
  box-shadow:0 1.75rem 4rem rgba(0,0,0,0.35);
}

.q-badge{
  display:inline-flex;align-items:center;gap:0.5rem;
  padding:0.4rem 0.75rem;border-radius:62.5rem;
  font-weight:950;font-size:0.85rem;
  color:rgba(255,255,255,0.90);
  background:rgba(255,255,255,0.06);
  border:0.0625rem solid rgba(255,255,255,0.14);
}

.q-card-title{font-weight:950;color:rgba(255,255,255,0.95);font-size:1.35rem;margin-top:0.75rem;}
.q-card-sub{color:rgba(255,255,255,0.70);font-weight:750;line-height:1.6;margin-top:0.35rem;}

/* Dropzone */
.q-drop{
  border-radius:1.5rem;padding:1.75rem 1.25rem;
  background:rgba(0,0,0,0.22);
  border:0.0625rem dashed rgba(255,255,255,0.20);
  position:relative;
}
.q-drop-active{border-color:rgba(255,255,255,0.35);background:rgba(255,255,255,0.05);}

.q-drop-inner{text-align:center;}
.q-drop-ic{
  width:3.25rem;height:3.25rem;border-radius:1.35rem;
  display:inline-flex;align-items:center;justify-content:center;
  background:rgba(255,255,255,0.06);
  border:0.0625rem solid rgba(255,255,255,0.10);
  color:rgba(255,255,255,0.92);font-size:1.4rem;
}
.q-drop-title{font-weight:950;color:rgba(255,255,255,0.94);}
.q-drop-sub{font-weight:800;color:rgba(255,255,255,0.65);}

.q-file{position:absolute;inset:0;opacity:0;cursor:pointer;}
.q-hint{color:rgba(255,255,255,0.60);font-weight:750;line-height:1.55;}

/* picked file */
.q-file-picked{text-align:left;}
.q-file-row{
  display:flex;gap:0.9rem;align-items:center;
  padding:1rem;border-radius:1.25rem;
  background:rgba(255,255,255,0.04);
  border:0.0625rem solid rgba(255,255,255,0.10);
}
.q-file-ic{
  width:2.75rem;height:2.75rem;border-radius:1.1rem;
  display:inline-flex;align-items:center;justify-content:center;
  background:rgba(255,255,255,0.06);
  border:0.0625rem solid rgba(255,255,255,0.10);
  color:rgba(255,255,255,0.9);font-size:1.2rem;
}
.q-file-name{font-weight:950;color:rgba(255,255,255,0.94);}
.q-file-sub{font-weight:800;color:rgba(255,255,255,0.62);}

.q-x{
  margin-left:auto;border-radius:1rem;padding:0.6rem 0.8rem;
  border:0.0625rem solid rgba(255,255,255,0.16);
  background:rgba(0,0,0,0.25);color:rgba(255,255,255,0.85);
}
.q-x:hover{background:rgba(255,255,255,0.06);}

/* preview */
.q-preview{
  border-radius:1.5rem;overflow:hidden;
  border:0.0625rem solid rgba(255,255,255,0.10);
  background:rgba(0,0,0,0.22);
}
.q-preview-img{width:100%;height:auto;display:block;}
.q-preview-pdf{padding:1.25rem;color:rgba(255,255,255,0.75);font-weight:850;}

/* Labels / inputs */
.q-label{color:rgba(255,255,255,0.78);font-weight:950;letter-spacing:-0.01rem;display:block;margin-bottom:0.5rem;}
.q-input{
  border-radius:1.25rem;padding:0.85rem 1rem;
  border:0.0625rem solid rgba(255,255,255,0.14);
  background:linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02)), rgba(0,0,0,0.28);
  color:rgba(255,255,255,0.92);font-weight:850;
}
.q-input:focus{outline:none;border-color:rgba(255,255,255,0.28);box-shadow:none;}
.form-control:focus{box-shadow:none;}

/* --------- CUSTOM DROPDOWNS (professional) --------- */
.q-ddwrap{position:relative;}
.q-ddbtn{
  width:100%;
  border:0.0625rem solid rgba(255,255,255,0.14);
  background:
    linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02)),
    rgba(0,0,0,0.28);
  border-radius:1.35rem;
  padding:0.85rem 1rem;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:1rem;
  color:rgba(255,255,255,0.92);
  font-weight:950;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, box-shadow 160ms ease;
}
.q-ddbtn:hover{
  border-color:rgba(255,255,255,0.22);
  transform: translateY(-0.04rem);
}
.q-ddbtn:focus{
  outline:none;
  border-color:rgba(255,255,255,0.28);
  box-shadow: 0 0 0 0.25rem rgba(124,58,237,0.14);
}

.q-ddbtn-left{display:flex;align-items:center;gap:0.85rem;min-width:0;}
.q-ddicon{
  width:2.45rem;height:2.45rem;border-radius:1.05rem;
  display:inline-flex;align-items:center;justify-content:center;
  background:rgba(255,255,255,0.06);
  border:0.0625rem solid rgba(255,255,255,0.10);
  flex:0 0 auto;
}
.q-ddtext{display:flex;flex-direction:column;gap:0.1rem;min-width:0;text-align:left;}
.q-ddtop{font-size:0.82rem;font-weight:900;color:rgba(255,255,255,0.62);line-height:1.1;}
.q-ddbottom{
  font-size:1rem;font-weight:950;color:rgba(255,255,255,0.92);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
}

.q-ddchev{display:inline-flex;align-items:center;justify-content:center;transition:transform 160ms ease;opacity:0.85;}
.q-ddchev.on{transform:rotate(180deg);}

.q-ddmenu{
  position:absolute;
  left:0; right:0;
  top:calc(100% + 0.6rem);
  padding:0.5rem;
  border-radius:1.35rem;
  background: rgba(10, 12, 18, 0.96);
  border:0.0625rem solid rgba(255,255,255,0.12);
  box-shadow: 0 1.75rem 3.5rem rgba(0,0,0,0.55);
  z-index: 2000;

  transform-origin: top;
  animation: q-pop 140ms ease both;
}

.q-dditem{
  width:100%;
  text-align:left;
  border:0;
  background:transparent;
  color:rgba(255,255,255,0.88);
  border-radius:1.1rem;
  padding:0.85rem 0.9rem;
  display:grid;
  grid-template-columns: 1fr auto;
  gap:0.5rem 0.75rem;
  transition: background 140ms ease, transform 140ms ease;
}
.q-dditem:hover{
  background: rgba(255,255,255,0.06);
  transform: translateY(-0.03rem);
}
.q-dditem.active{
  background: linear-gradient(90deg, rgba(45,108,255,0.18), rgba(124,58,237,0.18), rgba(255,61,141,0.14));
  border: 0.0625rem solid rgba(255,255,255,0.10);
}
.q-dditem-title{font-weight:950;}
.q-dditem-hint{
  grid-column: 1 / -1;
  font-size:0.9rem;
  font-weight:800;
  color:rgba(255,255,255,0.62);
  line-height:1.35;
}
.q-ddcheck{align-self:start; opacity:0.95;}

.q-ddbackdrop{
  position:fixed;
  inset:0;
  background:transparent;
  border:0;
  z-index: 1500;
}

/* Results / rest (same as your original; kept minimal here) */
.q-results-top{display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;}
.q-status{
  display:inline-flex;align-items:center;gap:0.5rem;
  padding:0.45rem 0.75rem;border-radius:62.5rem;
  border:0.0625rem solid rgba(255,255,255,0.14);
  background:rgba(0,0,0,0.22);
  color:rgba(255,255,255,0.72);
  font-weight:900;
}
.q-status-dot{width:0.6rem;height:0.6rem;border-radius:999px;background:rgba(255,255,255,0.45);}
.q-status-on{color:rgba(255,255,255,0.9);}
.q-status-on .q-status-dot{background:#ff3d8d;box-shadow:0 0 0 0.35rem rgba(255,61,141,0.18);}

.q-empty{text-align:center;padding:2.25rem 1rem;}
.q-empty-ic{
  width:3.25rem;height:3.25rem;border-radius:1.35rem;
  display:inline-flex;align-items:center;justify-content:center;
  background:rgba(255,255,255,0.06);
  border:0.0625rem solid rgba(255,255,255,0.10);
  color:rgba(255,255,255,0.92);font-size:1.4rem;
}
.q-empty-title{font-weight:950;color:rgba(255,255,255,0.94);}
.q-empty-sub{font-weight:800;color:rgba(255,255,255,0.65);line-height:1.6;}

.q-tabs{
  display:flex;gap:0.5rem;flex-wrap:wrap;
  padding:0.35rem;border-radius:1.25rem;
  background:rgba(0,0,0,0.22);
  border:0.0625rem solid rgba(255,255,255,0.10);
}
.q-tab{
  border-radius:1rem;padding:0.6rem 0.9rem;
  font-weight:950;color:rgba(255,255,255,0.75);
  border:0;background:transparent;
}
.q-tab.active{color:rgba(255,255,255,0.95);background:rgba(255,255,255,0.06);}

.q-pane{
  border-radius:1.5rem;padding:1.25rem;
  background:rgba(0,0,0,0.22);
  border:0.0625rem solid rgba(255,255,255,0.10);
}
.q-pane-title{font-weight:950;color:rgba(255,255,255,0.95);}

.q-bullets{margin:0;padding-left:1.2rem;color:rgba(255,255,255,0.78);font-weight:800;line-height:1.7;}

.q-actions{display:flex;gap:0.75rem;flex-wrap:wrap;}

.q-cta{
  border-radius:62.5rem;padding:0.85rem 1.25rem;font-weight:950;
  color:#fff;border:0;
  background:linear-gradient(90deg,#2d6cff 0%,#7c3aed 55%,#ff3d8d 120%);
  box-shadow:0 1.125rem 2.25rem rgba(0,0,0,0.35);
}
.q-cta:hover{filter:brightness(1.06);transform:translateY(-0.0625rem);}
.q-cta:disabled{opacity:0.6;filter:none;transform:none;}

.q-secondary{
  border-radius:62.5rem;padding:0.85rem 1.25rem;font-weight:900;
  color:rgba(255,255,255,0.92);
  border:0.0625rem solid rgba(255,255,255,0.18);
  background:rgba(255,255,255,0.04);
}
.q-secondary:hover{background:rgba(255,255,255,0.08);}

.q-btn{
  border-radius:62.5rem;padding:0.85rem 1.15rem;font-weight:950;
  color:rgba(255,255,255,0.92);
  border:0.0625rem solid rgba(255,255,255,0.18);
  background:rgba(255,255,255,0.04);
}
.q-btn:hover{background:rgba(255,255,255,0.08);}

.q-btn-soft{
  border-radius:62.5rem;padding:0.85rem 1.15rem;font-weight:950;
  color:rgba(255,255,255,0.95);border:0;
  background:linear-gradient(90deg, rgba(45,108,255,0.25), rgba(124,58,237,0.25), rgba(255,61,141,0.22));
}
.q-btn-soft:hover{filter:brightness(1.06);}

.q-download{
  border-radius:62.5rem;padding:0.85rem 1.2rem;font-weight:950;color:#fff;border:0;
  background:linear-gradient(90deg,#2d6cff 0%,#7c3aed 55%,#ff3d8d 120%);
  box-shadow:0 1.125rem 2.25rem rgba(0,0,0,0.35);
}
.q-download:hover{filter:brightness(1.06);transform:translateY(-0.0625rem);}

.q-upsell{background:rgba(255,255,255,0.03);}
.q-upsell-title{font-weight:950;color:rgba(255,255,255,0.95);}
.q-upsell-sub{font-weight:800;color:rgba(255,255,255,0.70);line-height:1.6;}

@keyframes q-pop{
  from{opacity:0;transform: translateY(-0.35rem) scale(0.98);}
  to{opacity:1;transform: translateY(0) scale(1);}
}

@media (max-width: 62rem){
  .q-sub{max-width:100%;}
  .q-actions > .btn{width:100%;}
}
</style>
